import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Divider,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { getActivity, type ActivityFilters, type ActivityItem, type ActivityMetrics } from '../../Services/activity';
import { renderActivityParts } from './activityLabels';

// Encart d'activité greffé sur une fiche admin (utilisateur, compte de facturation,
// projet, compte connecteur). UN SEUL composant pour les quatre : seuls changent les
// filtres envoyés au back et les colonnes de contexte qu'il est utile d'afficher.
//
// ⚠️ Les métriques sont calculées côté back avec LES MÊMES filtres d'entité que le
// flux. Sans ça on juxtaposerait les events d'un connecteur et l'engagement de tout le
// parc — des chiffres justes mais sans rapport avec les lignes qu'on lit.

// Plafond d'affichage. Envoyé au back (qui borne lui-même à 200) plutôt que tronqué
// côté client : inutile de transporter ce qu'on n'affichera pas.
const MAX_RESULTS = 100;

const PERIODS = [
	{ days: 30, label: '30 derniers jours' },
	{ days: 90, label: '90 derniers jours' },
	{ days: 365, label: '12 derniers mois' },
];

// Filtres portés par la fiche d'accueil. `days` et `limit` sont gérés par l'encart.
export type ActivityCardFilters = Omit<ActivityFilters, 'days' | 'limit' | 'offset'>;

export interface ActivityCardProps {
	filters: ActivityCardFilters;
	title?: string;
	// Colonnes de contexte : on masque celle qui répète le périmètre de la fiche
	// courante (le BA sur la fiche BA, le projet sur la fiche projet), sinon chaque
	// ligne affiche la même information et noie celle qui varie.
	showBillingAccount?: boolean;
	showProject?: boolean;
	// Lien vers la fiche de l'acteur. À couper sur la fiche utilisateur : le lien
	// pointerait sur la page où l'on se trouve déjà.
	linkActor?: boolean;
}

// #region formatters
const dayKey = (iso: string) => {
	const d = new Date(iso);
	return isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
};

const dayLabel = (key: string) => {
	const d = new Date(`${key}T12:00:00Z`);
	if (isNaN(d.getTime())) return key;
	const today = new Date().toISOString().slice(0, 10);
	const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
	if (key === today) return "Aujourd'hui";
	if (key === yesterday) return 'Hier';
	return d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });
};

const hourLabel = (iso: string) => {
	const d = new Date(iso);
	return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};
// #endregion

const linkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };

// Le nom de l'acteur est DANS la phrase : le souligner en permanence hacherait la
// lecture du fil. Gras suffit à signaler qu'il est cliquable.
const actorLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'none', fontWeight: 600 };

// #region ActivityRow
const ActivityRow = ({
	item,
	showBillingAccount,
	showProject,
	linkActor,
}: {
	item: ActivityItem;
	showBillingAccount: boolean;
	showProject: boolean;
	linkActor: boolean;
}) => {
	const { actor, text } = renderActivityParts(item);
	// Pas de lien si l'event est machine ou si l'acteur n'a pas d'id numérique (events
	// dont l'émetteur ne publie que l'UID Firebase) : on affiche alors le nom en texte.
	const actorUserId = linkActor ? item.actor.userId : undefined;

	return (
		<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.75 }}>
			<Box
				sx={{
					mt: '7px',
					width: 8,
					height: 8,
					borderRadius: '50%',
					flexShrink: 0,
					bgcolor: item.isMachine ? 'grey.400' : 'info.main',
				}}
			/>
			<Box sx={{ minWidth: 0, flexGrow: 1 }}>
				<Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
					{actor &&
						(actorUserId ? (
							<Link to={`/users/${actorUserId}`} style={actorLinkStyle}>
								{actor}
							</Link>
						) : (
							actor
						))}
					{actor ? ' ' : ''}
					{text}
				</Typography>
				<Typography variant="caption" color="text.secondary" component="div">
					{hourLabel(item.occurredAt)}
					{showBillingAccount && item.scope.billingAccountId && (
						<>
							{' · '}
							<Link to={`/billing-accounts/${item.scope.billingAccountId}`} style={linkStyle}>
								{item.scope.billingAccountName || item.scope.billingAccountId}
							</Link>
						</>
					)}
					{showProject && item.scope.projectId && (
						<>
							{' · '}
							<Link to={`/projects/${item.scope.projectId}`} style={linkStyle}>
								{item.scope.projectName || item.scope.projectFolderId}
							</Link>
						</>
					)}
				</Typography>
			</Box>
			<Chip
				size="small"
				variant="outlined"
				label={item.type}
				sx={{ flexShrink: 0, fontSize: '0.68rem', color: 'text.secondary' }}
			/>
		</Box>
	);
};
// #endregion

// #region ActivityCard
export const ActivityCard = ({
	filters,
	title = 'Activité',
	showBillingAccount = true,
	showProject = true,
	linkActor = true,
}: ActivityCardProps) => {
	const [days, setDays] = useState(90);
	const [items, setItems] = useState<ActivityItem[]>([]);
	const [total, setTotal] = useState(0);
	const [metrics, setMetrics] = useState<ActivityMetrics | null>(null);
	const [loading, setLoading] = useState(true);

	// `filters` est un objet littéral recréé à chaque render des fiches appelantes :
	// le mettre en dépendance de useCallback relancerait la requête en boucle. On
	// dépend donc de sa forme sérialisée, et on la relit ici — la dépendance reste
	// honnête (pas de filtre oublié) sans imposer un useMemo à chaque appelant.
	const filterKey = JSON.stringify(filters);

	const load = useCallback(async () => {
		setLoading(true);
		const res = await getActivity({ ...JSON.parse(filterKey), days, limit: MAX_RESULTS });
		setItems(res.feed.items);
		setTotal(res.feed.total);
		setMetrics(res.metrics);
		setLoading(false);
	}, [filterKey, days]);

	useEffect(() => {
		load();
	}, [load]);

	const groups = items.reduce<[string, ActivityItem[]][]>((acc, item) => {
		const key = dayKey(item.occurredAt);
		const last = acc[acc.length - 1];
		if (last && last[0] === key) last[1].push(item);
		else acc.push([key, [item]]);
		return acc;
	}, []);

	return (
		<Card sx={{ mt: 2 }}>
			<CardContent>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
					<Typography variant="h6">{title}</Typography>
					{metrics && (
						<>
							<Chip size="small" label={`${total} événements`} color="primary" />
							<Chip size="small" label={`Jours actifs 7 j : ${metrics.l7}/7`} />
							<Chip size="small" label={`28 j : ${metrics.l28}/28`} />
							<Chip
								size="small"
								label={`Tendance : ${metrics.trendL7 > 0 ? '+' : ''}${metrics.trendL7}`}
								color={metrics.trendL7 < 0 ? 'warning' : 'default'}
							/>
						</>
					)}
					<Box sx={{ flexGrow: 1 }} />
					<TextField
						select
						size="small"
						label="Période"
						value={days}
						onChange={(e) => setDays(Number(e.target.value))}
						// fullWidth={false} explicite : le thème react-admin impose fullWidth
						// aux champs de formulaire, ce qui casserait l'alignement de l'en-tête.
						fullWidth={false}
						sx={{ width: 190, flex: '0 0 auto' }}
					>
						{PERIODS.map((p) => (
							<MenuItem key={p.days} value={p.days}>
								{p.label}
							</MenuItem>
						))}
					</TextField>
				</Box>

				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
						<CircularProgress size={22} />
					</Box>
				)}

				{!loading && items.length === 0 && (
					<Typography variant="body2" color="text.secondary">
						Aucune activité sur cette période.
					</Typography>
				)}

				{!loading &&
					groups.map(([day, dayItems]) => (
						<Box key={day} sx={{ mb: 1.5 }}>
							<Typography
								variant="overline"
								color="text.secondary"
								sx={{ display: 'block', fontWeight: 700 }}
							>
								{dayLabel(day)}
							</Typography>
							<Divider sx={{ mb: 0.5 }} />
							{dayItems.map((item, i) => (
								<ActivityRow
									key={`${item.type}-${item.occurredAt}-${i}`}
									item={item}
									showBillingAccount={showBillingAccount}
									showProject={showProject}
									linkActor={linkActor}
								/>
							))}
						</Box>
					))}

				{/* Affichage plafonné : on le DIT, sinon « 100 lignes » se lit comme
				    « toute l'activité » et fausse le jugement. */}
				{!loading && total > items.length && (
					<Typography variant="caption" color="text.secondary">
						{items.length} événements affichés sur {total} — limité à {MAX_RESULTS}. Réduis la période pour
						affiner.
					</Typography>
				)}
			</CardContent>
		</Card>
	);
};
// #endregion
